import { Action, Subject } from '@erp/authz';
import { useCan } from 'src/hooks/useCan';

/** The task fields that decide whether it can be detached from an agent. */
export interface DetachableTask {
  status: string;
  assigneeAgentDefinitionId: string | null;
  metadata: Record<string, unknown> | null;
  deletedAt?: Date | string | null;
}

const DETACHABLE_STATUSES = new Set(['pending', 'in_progress', 'needs_input']);

/**
 * The agent an open task can be detached from: its agent assignee, else the
 * agent that escalated it. Null when there is nothing to detach. Mirrors the
 * backend rule in `detachTaskFromAgent`.
 */
export function detachableAgentId(task: DetachableTask): string | null {
  if (task.deletedAt != null || !DETACHABLE_STATUSES.has(task.status)) {
    return null;
  }
  if (task.assigneeAgentDefinitionId) return task.assigneeAgentDefinitionId;
  const escalatedFrom = task.metadata?.['escalatedFromAgentDefinitionId'];
  return typeof escalatedFrom === 'string' && escalatedFrom
    ? escalatedFrom
    : null;
}

/** {@link detachableAgentId}, or null when the caller may not update tasks. */
export function useDetachableAgentId(task: DetachableTask): string | null {
  const [canUpdateTasks] = useCan(Action.Update, Subject.Task);
  return canUpdateTasks ? detachableAgentId(task) : null;
}
